// Small Tailwind-utility primitives shared across the dashboard.
// Radix Slot is used by <Button asChild> so callers can render an anchor or
// other element while keeping button styles.

import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import * as SelectPrimitive from '@radix-ui/react-select'
import { cx } from '../lib/cx'

type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'danger'
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  asChild?: boolean
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent-info/15 text-accent-info border border-accent-info/40 hover:bg-accent-info/25',
  ghost: 'text-ink-muted hover:text-ink hover:bg-panel-raised',
  outline: 'border border-panel-border text-ink hover:bg-panel-raised',
  danger: 'bg-accent-danger/15 text-accent-danger border border-accent-danger/40 hover:bg-accent-danger/25',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', asChild, className, ...props },
  ref,
) {
  const cls = cx(
    'inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-info/60 disabled:opacity-50 disabled:pointer-events-none',
    VARIANTS[variant],
    className,
  )
  if (asChild) {
    return (
      <Slot className={cls}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {(props as any).children}
      </Slot>
    )
  }
  return <button ref={ref} className={cls} {...props} />
})

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cx('h-8 rounded-md border border-panel-border bg-panel-raised px-2.5 text-xs text-ink outline-none placeholder:text-ink-muted focus-visible:ring-2 focus-visible:ring-accent-info/60', className)} {...props} />
})

export const Select = SelectPrimitive.Root
export const SelectValue = SelectPrimitive.Value

export const SelectTrigger = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>>(function SelectTrigger({ className, children, ...props }, ref) {
  return <SelectPrimitive.Trigger ref={ref} className={cx('inline-flex h-8 min-w-24 items-center justify-between gap-2 rounded-md border border-panel-border bg-panel-raised px-2.5 text-xs text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent-info/60', className)} {...props}>{children}<SelectPrimitive.Icon aria-hidden>⌄</SelectPrimitive.Icon></SelectPrimitive.Trigger>
})

export const SelectContent = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>>(function SelectContent({ className, children, position = 'popper', ...props }, ref) {
  return <SelectPrimitive.Portal><SelectPrimitive.Content ref={ref} position={position} className={cx('z-50 min-w-32 overflow-hidden rounded-md border border-panel-border bg-panel p-1 text-xs text-ink shadow-panel', className)} {...props}><SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport></SelectPrimitive.Content></SelectPrimitive.Portal>
})

export const SelectItem = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>>(function SelectItem({ className, children, ...props }, ref) {
  return <SelectPrimitive.Item ref={ref} className={cx('relative flex cursor-default select-none items-center rounded px-2 py-1.5 outline-none data-[highlighted]:bg-panel-raised data-[highlighted]:text-ink', className)} {...props}><SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText></SelectPrimitive.Item>
})

export function Badge({ tone = 'muted', children }: { tone?: 'ok' | 'warn' | 'danger' | 'info' | 'muted'; children: React.ReactNode }) {
  const tones: Record<string, string> = {
    ok: 'bg-accent-ok/15 text-accent-ok border-accent-ok/30',
    warn: 'bg-accent-warn/15 text-accent-warn border-accent-warn/30',
    danger: 'bg-accent-danger/15 text-accent-danger border-accent-danger/30',
    info: 'bg-accent-info/15 text-accent-info border-accent-info/30',
    muted: 'bg-panel-raised text-ink-muted border-panel-border',
  }
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium', tones[tone])}>
      {children}
    </span>
  )
}

export function Panel({ title, actions, children, className }: { title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cx('rounded-lg border border-panel-border bg-panel shadow-panel', className)}>
      {title && (
        <header className="flex items-center justify-between gap-2 border-b border-panel-border px-4 py-3">
          <h2 className="text-sm font-semibold tracking-wide text-ink">{title}</h2>
          {actions}
        </header>
      )}
      <div className="px-4 py-3">{children}</div>
    </section>
  )
}
